import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import api from "../../../../utils/apiConfig";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { useB2BFormLoadingState } from "../../B2BShared/hooks/useB2BLoadingState";
import { B2BFormField, useB2BFormField } from "../../B2BShared/components/B2BFormField";
import "./B2BRegisterForm.css";

const B2BRegisterForm = () => {
  const {
    isLoading,
    error,
    handleSubmit: handleFormSubmit,
    setError
  } = useB2BFormLoadingState();
  const navigate = useNavigate();
  const { t, tAuth } = useB2BTranslation();

  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setError: setFieldError,
    setValue,
    getValues,
    clearErrors,
    trigger
  } = useForm({
    defaultValues: {
      name: "",
      surname: "",
      email: "",
      countryCode: "+44", // Default to UK for UK-based platform
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
      gdprConsent: false,
      marketingConsent: false,
    },
    mode: "onChange",
  });

  // Initialize B2BFormField hook with the form instance
  // Pass the entire form object, not individual methods
  const formMethods = {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
    trigger
  };
  
  const { getFieldProps } = useB2BFormField(formMethods);

  // Validation is now handled by B2BFormField system

  // ✅ MIGRATED: Using consolidated country codes from B2BShared/constants/B2BCountryCodes

  const onSubmit = async (values: any) => {
    // Client-side validation - ensure all required fields are filled
    if (!values.name || !values.name.trim()) {
      setFieldError("name", { type: "required", message: "Name is required" });
      return;
    }
    if (!values.surname || !values.surname.trim()) {
      setFieldError("surname", { type: "required", message: "Surname is required" });
      return;
    }
    if (!values.email || !values.email.trim()) {
      setFieldError("email", { type: "required", message: "Email is required" });
      return;
    }
    if (!values.phoneNumber || !values.phoneNumber.trim()) {
      setFieldError("phoneNumber", { type: "required", message: "Phone number is required" });
      return;
    }
    if (!values.password || !values.password.trim()) {
      setFieldError("password", { type: "required", message: "Password is required" });
      return;
    }
    if (!values.confirmPassword || !values.confirmPassword.trim()) {
      setFieldError("confirmPassword", { type: "required", message: "Please confirm your password" });
      return;
    }
    if (!values.termsAccepted) {
      setFieldError("termsAccepted", { type: "required", message: "You must accept the terms and conditions" });
      return;
    }
    if (!values.gdprConsent) {
      setFieldError("gdprConsent", { type: "required", message: "You must accept the privacy policy" });
      return;
    }

    try {
      return await handleFormSubmit(async () => {
        // Split combined phone number back into country code and phone number
        const phoneValue = values.phoneNumber || "";
        const spaceIdx = phoneValue.indexOf(" ");
        let countryCode = "+44";
        let cleanPhoneNumber = "";
        
        if (spaceIdx > 0) {
          const ccPart = phoneValue.substring(0, spaceIdx).trim();
          if (ccPart.startsWith("+")) countryCode = ccPart;
          cleanPhoneNumber = phoneValue.substring(spaceIdx + 1);
        } else {
          cleanPhoneNumber = phoneValue;
        }
        
        // Normalize: strip spaces, dashes, parentheses, dots — keep only digits
        cleanPhoneNumber = cleanPhoneNumber.replace(/[\s\-().]/g, "");
        countryCode = countryCode.replace(/[\s\-().]/g, "");

        const registrationData = {
          name: values.name.trim(),
          surname: values.surname.trim(),
          email: values.email.trim().toLowerCase(),
          countryCode: countryCode,
          phoneNumber: cleanPhoneNumber, // Send clean phone number without country code
          password: values.password,
          confirmPassword: values.confirmPassword,
          termsAccepted: Boolean(values.termsAccepted),
          gdprConsent: Boolean(values.gdprConsent),
          marketingConsent: Boolean(values.marketingConsent),
          consentTimestamp: new Date().toISOString(),
          privacyPolicyVersion: "1.0", // Current version of privacy policy
        };

        const response = await api.register(registrationData);

        if (response.success) {
          // Registration successful - authenticate user and navigate to email verification
          // Store token and user data in localStorage
          localStorage.setItem("b2bToken", response.data.token);
          localStorage.setItem("b2bUser", JSON.stringify(response.data.user));
          
          // Navigate to email verification
          navigate("/gcb/email-verification", {
            state: {
              email: registrationData.email,
              userId: response.data.user._id,
            },
          });
        }
      });
    } catch (err: any) {
      // Catch the error from handleFormSubmit to prevent uncaught runtime error
      if (err.response?.data?.error) {
        // Handle specific error cases
        if (err.response.data.error === "User with this email already exists" || err.response.data.error.includes("already exists")) {
          // Set a special flag to render the structured error message
          setError("EMAIL_EXISTS");
        } else if (err.response.data.error === "Account pending approval" || err.response?.data?.code === 'PENDING_APPROVAL') {
          // If user tries to register but account is pending approval, redirect to waiting page
          // Store token and user data if available in error response
          if (err.response?.data?.token) {
            localStorage.setItem("b2bToken", err.response.data.token);
          }
          if (err.response?.data?.user) {
            localStorage.setItem("b2bUser", JSON.stringify(err.response.data.user));
          }
          
          navigate("/gcb/waiting-approval");
        } else {
          setError(err.response.data.error);
        }
      } else if (err.response?.data?.fieldErrors) {
        // Handle field-specific errors
        Object.keys(err.response.data.fieldErrors).forEach((field) => {
          setFieldError(field as any, err.response.data.fieldErrors[field]);
        });
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        // Generic error — usually means the server is asleep or unreachable
        const errorMsg = (err.message || "").toLowerCase();
        if (errorMsg.includes("load failed") || errorMsg.includes("failed to fetch") || errorMsg.includes("network")) {
          setError("The server is waking up. Please wait a few seconds and try again.");
        } else {
          setError(`Registration failed: ${err.message || t("registrationFailed")}. Please try again.`);
        }
      }
      // Don't re-throw - error is handled and displayed
    }
  };

  return (
    <div className="b2b-register-container">
      <div className="b2b-register-card">
        <div className="register-top-nav">
          <a href="/gcb" className="back-to-b2b-button">
            {t("backToB2BPage") || "Back to main GCB Page"}
          </a>
          <span className="register-signin-link">
            {tAuth("alreadyMember")}{" "}
            <a href="/gcb/login">{tAuth("signInHere")}</a>
          </span>
        </div>

        <div className="register-header">
          <h2>{tAuth("registerTitle")}</h2>
          <p className="register-subtitle">{tAuth("registerSubtitle")}</p>
        </div>

        {error && (
          <div className="error-message">
            {error === "EMAIL_EXISTS" ? (
              <div className="email-exists-error">
                <p className="error-main-text">{tAuth("emailAlreadyExists")}</p>
                <p className="error-help-text">
                  {tAuth("emailAlreadyExistsResetPassword")}{" "}
                  <Link to="/gcb/forgot-password" className="error-link">
                    {tAuth("emailAlreadyExistsResetPasswordLink")}
                  </Link>
                  .
                </p>
                <p className="error-help-text">
                  {tAuth("emailAlreadyExistsSignIn")}{" "}
                  <Link to="/gcb/login" className="error-link">
                    {tAuth("emailAlreadyExistsSignInLink")}
                  </Link>{" "}
                  {tAuth("emailAlreadyExistsOrUseDifferent")}
                </p>
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: error }}></div>
            )}
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="register-form"
        >
          <div className="form-section">
            <B2BFormField
              {...getFieldProps("name", "text")}
              label={tAuth("name")}
              placeholder={tAuth("namePlaceholder")}
              required
            />

            <B2BFormField
              {...getFieldProps("surname", "text")}
              label={tAuth("surname")}
              placeholder={tAuth("surnamePlaceholder")}
              required
            />

            <B2BFormField
              {...getFieldProps("email", "email")}
              label={tAuth("email")}
              placeholder={tAuth("emailPlaceholder")}
              required
            />

            <B2BFormField
              {...getFieldProps("phoneNumber", "phone")}
              label={tAuth("phoneNumber")}
              placeholder={tAuth("phoneNumberPlaceholder")}
              required
            />
          </div>

          <div className="form-section">
            <B2BFormField
              {...getFieldProps("password", "password")}
              label={tAuth("password")}
              placeholder={tAuth("createPasswordPlaceholder")}
              required
              showPasswordToggle
              passwordRequirements={{
                minLength: 8,
                requireUppercase: true,
                requireLowercase: true,
                requireNumber: true,
                requireSpecialChar: true,
              }}
            />

            <B2BFormField
              {...getFieldProps("confirmPassword", "password")}
              label={tAuth("confirmPassword")}
              placeholder={tAuth("confirmPasswordPlaceholder")}
              required
              showPasswordToggle
              confirmPassword
            />
          </div>

          {/* GDPR Compliance Section */}
          <div className="consent-section">
            <h3 className="consent-section-title">{tAuth("consentTitle")}</h3>
            
            <div className="form-group">
              <div className="form-checkbox">
                <input
                  {...register("termsAccepted", { required: "You must accept the terms and conditions" })}
                  type="checkbox"
                  id="termsAccepted"
                  className="form-checkbox-input"
                />
                <label htmlFor="termsAccepted" className="form-checkbox-label">
                  {tAuth("termsAcceptance")}
                  <br />
                  <a href="/gcb/terms" target="_blank" rel="noopener noreferrer" className="consent-link">
                    {tAuth("termsAndConditions")}
                  </a>
                  <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                </label>
              </div>
              {errors.termsAccepted && (
                <div className="field-error" role="alert">
                  {errors.termsAccepted.message}
                </div>
              )}
            </div>

            <div className="form-group">
              <div className="form-checkbox">
                <input
                  {...register("gdprConsent", { required: "You must accept the privacy policy" })}
                  type="checkbox"
                  id="gdprConsent"
                  className="form-checkbox-input"
                />
                <label htmlFor="gdprConsent" className="form-checkbox-label">
                  {tAuth("privacyAcceptance")}{" "}
                  <a href="/gcb/privacy" target="_blank" rel="noopener noreferrer" className="consent-link">
                    {tAuth("privacyPolicy")}
                  </a>
                  <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                </label>
              </div>
              {errors.gdprConsent && (
                <div className="field-error" role="alert">
                  {errors.gdprConsent.message}
                </div>
              )}
            </div>

            <div className="form-group">
              <div className="form-checkbox">
                <input
                  {...register("marketingConsent")}
                  type="checkbox"
                  id="marketingConsent"
                  className="form-checkbox-input"
                />
                <label htmlFor="marketingConsent" className="form-checkbox-label">
                  {tAuth("marketingConsent")}
                  <span className="optional-indicator">({t("validation.optional")})</span>
                </label>
              </div>
            </div>

            <div className="consent-info">
              <p className="consent-info-text">
                {tAuth("consentInfo")}
              </p>
            </div>
          </div>

          {/* Duplicate error message above register button */}
          {error && error === "EMAIL_EXISTS" && (
            <div className="error-message">
              <div className="email-exists-error">
                <p className="error-main-text">{tAuth("emailAlreadyExists")}</p>
                <p className="error-help-text">
                  {tAuth("emailAlreadyExistsResetPassword")}{" "}
                  <Link to="/gcb/forgot-password" className="error-link">
                    {tAuth("emailAlreadyExistsResetPasswordLink")}
                  </Link>
                  .
                </p>
                <p className="error-help-text">
                  {tAuth("emailAlreadyExistsSignIn")}{" "}
                  <Link to="/gcb/login" className="error-link">
                    {tAuth("emailAlreadyExistsSignInLink")}
                  </Link>{" "}
                  {tAuth("emailAlreadyExistsOrUseDifferent")}
                </p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="register-button"
          >
            {isLoading ? tAuth("registering") : tAuth("register")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default B2BRegisterForm;
